import React from "react";

// Asset URLs (served from /public)
export const LOGO_FULL = "/brand/seedkeepers-logo.png";
export const LOGO_MARK = "/brand/seedkeepers-mark.png";
export const LOGO_WORDMARK = "/brand/seedkeepers-wordmark.png";

/**
 * <Brand /> renders the SeedKeepers identity in one of four variants.
 *
 * variant:
 *   - "mark"       : just the seed/grass mark (square aspect)
 *   - "inline"     : small mark + 2-line "SeedKeepers / Provenance Intelligence" text
 *   - "stacked"    : centered, large — for hero / splash
 *   - "wordmark"   : wordmark only
 *
 * tone:
 *   - "default"    : dark text (use on cream / light backgrounds)
 *   - "light"      : light text (use on dark hero backgrounds — wraps mark in a soft pill)
 */
export default function Brand({
  variant = "inline",
  tone = "default",
  size = 40,
  subtitle = "Provenance Intelligence",
  className = "",
  testid = "brand",
}) {
  if (variant === "mark") {
    return (
      <img
        src={LOGO_MARK}
        alt="SeedKeepers"
        width={size}
        height={size}
        className={`object-contain ${className}`}
        data-testid={`${testid}-mark`}
      />
    );
  }

  if (variant === "wordmark") {
    return (
      <img
        src={LOGO_WORDMARK}
        alt="SeedKeepers"
        className={`object-contain ${className}`}
        style={{ height: size }}
        data-testid={`${testid}-wordmark`}
      />
    );
  }

  if (variant === "stacked") {
    return (
      <div className={`flex flex-col items-center text-center ${className}`} data-testid={`${testid}-stacked`}>
        <img
          src={LOGO_FULL}
          alt="SeedKeepers — Restoration begins with seed"
          className="object-contain"
          style={{ maxHeight: size, height: size, width: "auto" }}
        />
      </div>
    );
  }

  // inline (default)
  const darkText = tone === "default";
  const titleColor = darkText ? "text-[#2C2A28]" : "text-[#FDFBF7]";
  const subColor = darkText ? "text-[#8A857D]" : "text-[#FDFBF7]/80";
  const markBg = darkText
    ? "bg-[#FBF6EE] border border-[#E6E2D8]"
    : "bg-[#FDFBF7]/15 border border-white/20";

  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid={`${testid}-inline`}>
      <div
        className={`rounded-md flex items-center justify-center shrink-0 overflow-hidden ${markBg}`}
        style={{ width: size, height: size }}
      >
        <img
          src={LOGO_MARK}
          alt=""
          className="object-contain"
          style={{ width: Math.round(size * 0.78), height: Math.round(size * 0.78) }}
        />
      </div>
      <div className="leading-tight">
        <div className={`font-display font-bold tracking-tight text-[1.02rem] ${titleColor}`}>SeedKeepers</div>
        {subtitle && (
          <div className={`text-[10px] tracking-[0.22em] uppercase font-semibold ${subColor}`}>{subtitle}</div>
        )}
      </div>
    </div>
  );
}

/** Full-screen splash with the logo + SPIRIT branding — shown while auth is checking. */
export function SplashScreen({ message = "" }) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-background sk-contour px-6 text-center"
      data-testid="app-splash"
    >
      <img
        src={LOGO_FULL}
        alt="SeedKeepers — Restoration begins with seed"
        className="object-contain max-w-[300px] w-full opacity-0 animate-[sk-fade-in_500ms_ease-out_forwards]"
        style={{ animationDelay: "60ms" }}
      />
      <div
        className="mt-10 font-display text-[40px] sm:text-[52px] leading-none tracking-[0.18em] font-bold text-[#8B5A2B] opacity-0 animate-[sk-fade-in_500ms_ease-out_forwards]"
        style={{ animationDelay: "200ms" }}
      >
        SPIRIT
      </div>
      <div
        className="mt-3 max-w-md font-serif-display text-[15px] sm:text-[17px] leading-snug text-[#2C2A28] opacity-0 animate-[sk-fade-in_500ms_ease-out_forwards]"
        style={{ animationDelay: "340ms" }}
      >
        Seed Provenance Intelligence and Restoration Information Technology
      </div>
      {message && (
        <div
          className="mt-5 text-[11px] tracking-[0.24em] uppercase font-semibold text-[#8A857D] opacity-0 animate-[sk-fade-in_500ms_ease-out_forwards]"
          style={{ animationDelay: "480ms" }}
        >
          {message}
        </div>
      )}
    </div>
  );
}
